import { confirm, ConfirmType } from './components/confirm/Confirm';

interface AccountingSubscriptionsOptions {
    notInvoicedText: string;
}

interface AccountingSubscriptionsModuleArgs {
    options: AccountingSubscriptionsOptions;
}

interface SubscriptionDevice {
    name: string;
    lastInvoiceDate: string;
}

interface DeviceNamesArgs {
    checkedDevices: SubscriptionDevice[];
    addInvoiceDate?: boolean;
}

type ActionMessageFactory = (checkedDevices: SubscriptionDevice[]) => string;

interface ActionConfig {
    button: HTMLElement | null;
    action: string;
    createMessage: ActionMessageFactory;
}

function accountingSubscriptionsModule({ options }: AccountingSubscriptionsModuleArgs): void {
    const subscriptionForm = document.getElementById('subscription_form');
    if (!(subscriptionForm instanceof HTMLFormElement)) {
        throw new Error('Subscription form not found.');
    }

    function getCheckedDevices(): SubscriptionDevice[] {
        const devices: SubscriptionDevice[] = [];
        Array.from(
            document.getElementsByClassName('action-select') as HTMLCollectionOf<HTMLInputElement>
        ).forEach((element) => {
            if (element.checked) {
                devices.push({
                    name: element.value,
                    lastInvoiceDate: element.dataset?.date ?? options.notInvoicedText,
                });
            }
        });
        return devices;
    }

    function getDeviceNames({ checkedDevices, addInvoiceDate = false }: DeviceNamesArgs): string {
        let checkedDeviceList = '';
        checkedDevices.forEach((device) => {
            checkedDeviceList += addInvoiceDate
                ? `${device.name} - ${device.lastInvoiceDate}\n`
                : `${device.name}\n`;
        });
        return checkedDeviceList;
    }

    function createDualSimAction(
        button: HTMLElement | null,
        action: string,
        singularMessage: string,
        pluralMessage: string
    ): ActionConfig {
        return {
            button,
            action,
            createMessage(checkedDevices) {
                const deviceNames = getDeviceNames({ checkedDevices });
                const message = checkedDevices.length > 1 ? pluralMessage : singularMessage;
                return `${message}\n${deviceNames}`;
            },
        };
    }

    function setElementAttributes(element: HTMLInputElement, name: string): void {
        element.setAttribute('type', 'hidden');
        element.setAttribute('name', name);
        element.setAttribute('id', name);
        subscriptionForm!.appendChild(element);
    }

    const actions: ActionConfig[] = [
        {
            button: document.getElementById('submit_activate_subscriptions'),
            action: 'activate',
            createMessage(checkedDevices) {
                const deviceNames = getDeviceNames({ checkedDevices });
                if (checkedDevices.length > 1) {
                    return interpolate(gettext('ACTIVATE_MULTIPLE_SUBSCRIPTION'), [deviceNames]);
                }
                return interpolate(gettext('ACTIVATE_SUBSCRIPTION'), [deviceNames]);
            },
        },
        {
            button: document.getElementById('submit_end_subscriptions'),
            action: 'end',
            createMessage(checkedDevices) {
                if (checkedDevices.length > 1) {
                    const deviceNamesDate = getDeviceNames({
                        checkedDevices,
                        addInvoiceDate: true,
                    });
                    return interpolate(gettext('CANCEL_MULTIPLE_SUBSCRIPTION'), [deviceNamesDate]);
                }
                const deviceNames = getDeviceNames({ checkedDevices });
                return interpolate(gettext('CANCEL_SUBSCRIPTION'), [
                    deviceNames,
                    checkedDevices[0].lastInvoiceDate,
                ]);
            },
        },
        createDualSimAction(
            document.getElementById('submit_enable_dual_sim'),
            'enable_dual_sim',
            gettext('ENABLE_DUAL_SIM_FOR_SWARM'),
            gettext('ENABLE_DUAL_SIM_FOR_MULTIPLE_SWARMS')
        ),
        createDualSimAction(
            document.getElementById('submit_disable_dual_sim'),
            'disable_dual_sim',
            gettext('DISABLE_DUAL_SIM_FOR_SWARM'),
            gettext('DISABLE_DUAL_SIM_FOR_MULTIPLE_SWARMS')
        ),
    ];

    actions.forEach(({ button, action, createMessage }) => {
        if (!button) {
            return;
        }

        button.addEventListener('click', (event) => {
            event.preventDefault();
            const checkedDevices = getCheckedDevices();
            if (checkedDevices.length < 1) {
                confirm({
                    markdownMessage: gettext('NO_SWARM_DETECTED'),
                    type: ConfirmType.INFO,
                });
                return;
            }
            const actionInput = document.createElement('input');
            setElementAttributes(actionInput, action);
            confirm({
                markdownMessage: createMessage(checkedDevices),
                onConfirm() {
                    subscriptionForm.submit();
                },
                afterClose() {
                    if (actionInput.parentNode === subscriptionForm) {
                        subscriptionForm.removeChild(actionInput);
                    }
                },
            });
        });
        const actionInput = document.getElementById(action);
        if (actionInput) {
            subscriptionForm.removeChild(actionInput);
        }
    });
}

export default accountingSubscriptionsModule;
